import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  PasswordResetRequest,
  Role,
  UserCreateRequest,
  UserDetail,
  UserListItem,
  UserServicesUpdateRequest,
  UserUpdateRequest,
} from '@crm/shared';
import { api } from '../../lib/api';

export type UserListFilter = {
  active: 'true' | 'false' | 'all';
  role?: Role;
  search?: string;
};

export const usersKeys = {
  all: ['users'] as const,
  list: (f: UserListFilter) => ['users', 'list', f] as const,
  one: (id: number) => ['users', 'one', id] as const,
};

export function useUsersList(filter: UserListFilter) {
  return useQuery({
    queryKey: usersKeys.list(filter),
    queryFn: async () => {
      const params: Record<string, string> = { active: filter.active };
      if (filter.role) params.role = filter.role;
      if (filter.search) params.search = filter.search;
      const res = await api.get<UserListItem[]>('/users', { params });
      return res.data;
    },
  });
}

export function useUserDetail(id: number | null) {
  return useQuery({
    queryKey: id ? usersKeys.one(id) : ['users', 'one', 'noop'],
    enabled: id !== null,
    queryFn: async () => {
      const res = await api.get<UserDetail>(`/users/${id}`);
      return res.data;
    },
  });
}

export function useUserCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UserCreateRequest) => {
      const res = await api.post<UserDetail>('/users', input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: usersKeys.all }),
  });
}

export function useUserUpdate(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UserUpdateRequest) => {
      const res = await api.patch<UserDetail>(`/users/${id}`, input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: usersKeys.all }),
  });
}

export function useUserDeactivate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await api.delete<UserDetail>(`/users/${id}`);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: usersKeys.all }),
  });
}

export function useUserActivate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await api.post<UserDetail>(`/users/${id}/activate`);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: usersKeys.all }),
  });
}

export function useUserPasswordReset(id: number) {
  return useMutation({
    mutationFn: async (input: PasswordResetRequest) => {
      await api.post(`/users/${id}/password`, input);
    },
  });
}

export function useUserServicesSet(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UserServicesUpdateRequest) => {
      const res = await api.put<UserDetail>(`/users/${id}/services`, input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: usersKeys.all }),
  });
}
